import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../api';

function LogisticsPanel() {
  const [routes, setRoutes] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [ships, setShips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');

  // Form state
  const [srcSector, setSrcSector] = useState('');
  const [destSector, setDestSector] = useState('');
  const [resourceType, setResourceType] = useState('iron_ore');
  const [routeType, setRouteType] = useState('round_trip');

  const RESOURCE_OPTIONS = [
    // Layer 1 — Raw materials
    'iron_ore', 'copper_ore', 'titanium_ore', 'tungsten_ore',
    'silicon', 'rare_crystals', 'quantum_dust',
    'ice', 'hydrogen_gas', 'methane_gas', 'carbon', 'biomass',
    // Layer 2 — Primary processed
    'iron_plate', 'copper_plate', 'titanium_plate', 'tungsten_plate',
    'silicon_wafer', 'crystal_core', 'quantum_substrate',
    'water', 'refined_gas', 'polymer_base', 'bio_gel', 'carbon_block',
    // Layer 3 — Secondary materials
    'steel', 'heavy_alloy', 'reinforced_alloy',
    'basic_circuit', 'advanced_circuit', 'quantum_processor',
    'fuel_cell', 'high_density_fuel', 'bio_fuel',
    'industrial_polymer', 'composite_material',
    // Layer 4 — Components
    'hull_segment', 'structural_frame', 'engine_core', 'thruster', 'weapon_mount',
    'ai_core', 'control_unit', 'sensor_array', 'power_core', 'reactor_unit', 'shield_generator',
    // Layer 5 — Sub-assemblies
    'engine_module', 'hull_section', 'energy_system', 'ai_system',
    // Legacy
    'common_minerals', 'rare_elements', 'energy', 'credits',
  ];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [routeRes, secRes, fleetRes] = await Promise.all([
        api.getTransportRoutes(),
        api.getSectors(),
        api.getFleet(),
      ]);
      setRoutes(routeRes.data.routes || []);
      setSectors(secRes.data.sectors || []);
      setShips((fleetRes.data.ships || []).filter(s => s.status === 'idle'));
    } catch (err) {
      console.error('Load logistics error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateRoute = async () => {
    if (!srcSector || !destSector || !resourceType) return;
    try {
      await api.createTransportRoute(Number(srcSector), Number(destSector), resourceType, routeType);
      setActionMsg('Route created!');
      setSrcSector(''); setDestSector('');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Create failed');
    }
  };

  const handleDeleteRoute = async (id) => {
    try {
      await api.deleteTransportRoute(id);
      setActionMsg('Route deleted.');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleToggle = async (id, current) => {
    try {
      await api.toggleTransportRoute(id, !current);
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Toggle failed');
    }
  };

  const handleAssign = async (routeId, shipId) => {
    try {
      await api.assignTransportShip(routeId, shipId);
      setActionMsg('Ship assigned to route.');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Assign failed');
    }
  };

  const handleUnassign = async (tsId) => {
    try {
      await api.unassignTransportShip(tsId);
      setActionMsg('Ship unassigned.');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Unassign failed');
    }
  };

  const sectorName = (id) => {
    const s = sectors.find(sec => sec.id === id);
    return s ? s.name : `Sector #${id}`;
  };

  if (loading) return <div className="panel-loading">Loading logistics...</div>;

  return (
    <div className="panel-container logistics-panel">
      <div className="panel-header">
        <h2>⇄ Logistics</h2>
      </div>

      {actionMsg && <div className="action-msg">{actionMsg}<button className="msg-dismiss" onClick={() => setActionMsg('')}>×</button></div>}

      {/* Create Route */}
      <div className="section build-section">
        <h3 className="section-title"><span className="title-accent">+</span> Create Transport Route</h3>
        <div className="form-grid">
          <div className="form-field">
            <label>From:</label>
            <select value={srcSector} onChange={e => setSrcSector(e.target.value)}>
              <option value="">Select...</option>
              {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>To:</label>
            <select value={destSector} onChange={e => setDestSector(e.target.value)}>
              <option value="">Select...</option>
              {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Resource:</label>
            <select value={resourceType} onChange={e => setResourceType(e.target.value)}>
              {RESOURCE_OPTIONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Type:</label>
            <select value={routeType} onChange={e => setRouteType(e.target.value)}>
              <option value="one_way">One Way</option>
              <option value="round_trip">Round Trip</option>
            </select>
          </div>
          <button className="btn btn-glow" onClick={handleCreateRoute}>Create Route</button>
        </div>
      </div>

      {/* Route List */}
      <div className="route-list">
        <h3 className="section-title"><span className="title-accent">⇄</span> Active Routes</h3>
        {routes.length === 0 && <p className="hud-empty">No routes configured. Create one above to start moving resources.</p>}
        {routes.map(r => (
          <div key={r.id} className={`section route-card ${r.is_active ? 'active' : 'inactive'}`}>
            <div className="route-header">
              <span className={`route-status-dot ${r.is_active ? 'active' : ''}`} />
              <span className="route-path">
                {sectorName(r.source_sector_id)} → {sectorName(r.dest_sector_id)}
              </span>
              <span className="badge">{r.resource_type.replace(/_/g, ' ')}</span>
              <span className="route-type-badge">{r.route_type === 'round_trip' ? '🔄' : '➡'} {r.route_type.replace(/_/g, ' ')}</span>
            </div>

            {/* Transport Ships */}
            <div className="route-ships">
              {r.transportShips && r.transportShips.map(ts => (
                <div key={ts.id} className="transport-ship-item">
                  <span>{ts.ship?.name || `Ship #${ts.ship_id}`}</span>
                  <span className="ts-status">{ts.status}</span>
                  <span className="ts-cargo">{ts.cargo_load || 0} cargo</span>
                  <button className="btn btn-sm btn-danger" onClick={() => handleUnassign(ts.id)}>Remove</button>
                </div>
              ))}
              {ships.length > 0 && (
                <div className="assign-ship">
                  <select id={`assign-${r.id}`} defaultValue="">
                    <option value="" disabled>Assign ship...</option>
                    {ships.map(s => <option key={s.id} value={s.id}>{s.name} (cargo: {s.cargo_capacity})</option>)}
                  </select>
                  <button className="btn btn-sm" onClick={() => {
                    const sel = document.getElementById(`assign-${r.id}`);
                    if (sel.value) handleAssign(r.id, Number(sel.value));
                  }}>Assign</button>
                </div>
              )}
            </div>

            <div className="route-actions">
              <button className="btn btn-sm" onClick={() => handleToggle(r.id, r.is_active)}>
                {r.is_active ? 'Pause' : 'Resume'}
              </button>
              <button className="btn btn-sm btn-danger" onClick={() => handleDeleteRoute(r.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default LogisticsPanel;
